import React, { useContext, useMemo } from 'react';
import Styled from 'styled-components';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { Info } from 'lucide-react';
import { isPast } from 'date-fns';
import { format } from 'date-fns/format';
import { Trans } from '@lingui/react';
import { sv, nb, fi, enGB } from 'date-fns/locale';
import { Flex } from '@components/layout/Grid';
import LightningIcon from '@assets/LightingIcon';
import LanguageContext from '@lib/contexts/languageContext';
import { LinkToRecording, LiveEvent } from '@gql/generated';
import { Button } from '@components/ButtonV2';
import { UserContext } from '@lib/contexts/UserProvider';

const DynamicVideoPlayer = dynamic(() => import('@organisms/video-player'), {
  ssr: false,
});

const Tag = Styled.span<{ outline?: boolean; plain?: boolean; full?: boolean }>`
  display: flex;
  align-items: center;
  font-size: ${({ plain }) => (plain ? 14 : 11)}px;
  color: ${({ full }) => (full ? 'white' : '#1c343e')};
  font-weight: 600;
  line-height: 1;
  ${({ plain }) => (plain ? '' : 'border: 1px solid #1c343e;')}
  border-radius: 3px;
  padding: 5px;
  margin-right: 5px;
  background-color: ${({ full }) => (full ? '#1c343e' : 'transparent')};

  svg {
    margin-right: 6px;
  }

  :empty {
    display: none;
  }
`;

const EventTitle = Styled.h1`
  font-size: 24px;
  color: #243240;
  font-weight: bold;
  line-height: 1.3;
  margin-bottom: 0;
`;

const localeMap = {
  en: enGB,
  se: sv,
  no: nb,
  fi,
};

type Props = {
  event: LiveEvent;
  showRecording?: boolean;
};

const EventRecording = ({ event, showRecording }: Props) => {
  const lang = useContext(LanguageContext);
  const [currentUser] = useContext(UserContext);

  const recordings = useMemo(() => {
    return event.linkToRecordings.filter((recording) => !!recording.video);
  }, [event.linkToRecordings]);
  const video = recordings[0]?.video;

  if (!video) {
    return null;
  }

  const expirationDate = recordings[0]?.expiresAt;
  const recordingExpired = isPast(new Date(expirationDate));
  const fullDate = format(expirationDate, "dd MMMM yyyy 'at' kk:mm O", {
    locale: localeMap[lang],
  });

  return (
    <section className="flex flex-col items-center lg:flex-row lg:gap-8 w-full pt-4 lg:pt-2">
      <div className="w-full relative flex-1">
        <div className="bg-neutral-100">
          {showRecording && !recordingExpired ? (
            <DynamicVideoPlayer
              kind={video.kind}
              autoPlay={false}
              isLimited={!video.hasAccess}
              key={video.id}
              id={video.id}
              title={video.content.title}
              description={video.content.shortDescription}
              videoSources={video.player.assets}
              thumbnailImage={video.assets.thumbnailUrl}
              video={video}
              onPlayStart={() => {
                //
              }}
            />
          ) : (
            <div className="">
              <img
                src={event.thumbnail}
                className="block aspect-video object-cover size-full rounded"
              />
            </div>
          )}
        </div>
      </div>
      <div className="flex-1 flex flex-col justify-center py-8">
        <Flex>
          <Tag outline>{event.duration} MIN</Tag>
          <Tag plain>
            <LightningIcon />
            <span className="first-letter:capitalize">
              {String(event.themeLabel)}
            </span>
          </Tag>
        </Flex>
        <EventTitle>
          <Trans id="live.event_page.recording_section.recording_label" />:{' '}
          {event.title}
        </EventTitle>
        {video.longDescription ? (
          <p className="py-2 m-0">
            {video.content.longDescription || video.content.shortDescription}
          </p>
        ) : null}
        <p className="text-pretty text-sm/6 max-w-2xl">
          <Info className="inline-block mr-1 size-4 -mb-0.5" />
          {recordingExpired ? (
            <Trans
              id="live.event_page.recording_section.recording_expired_message"
              values={{ fullDate }}
              components={[
                <Link
                  key="0"
                  className="text-primary font-medium px-1"
                  href={`/${lang}/live`}
                >
                  {null}
                </Link>,
              ]}
            />
          ) : (
            <Trans
              id="live.event_page.recording_section.recording_message"
              values={{ fullDate }}
              components={[<span key="0" className="text-nowrap"></span>]}
            />
          )}
        </p>

        {!recordingExpired ? (
          <div className="pb-5">
            <Button href={`/${lang}/live`} outline>
              <Trans id="live.event_page.all_event_link" />
            </Button>
          </div>
        ) : null}

        {!currentUser?.isNoneTrialPremium ? (
          <div className="flex flex-col sm:flex-row items-start md:items-center gap-2 w-full px-4 py-4 mt-2 bg-blue-100 rounded">
            <p className="flex  m-0 text-pretty pr-8">
              <Trans id="live.event_page.recording_section.upgrade_message" />
            </p>
            <Button
              className="flex-shrink-0"
              size="small"
              href={`/${lang}/subscription/select`}
            >
              <Trans id="live.event_page.recording_section.upgrade_now_btn" />
            </Button>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default EventRecording;
